import { DateTime, Info, type WeekdayNumbers } from 'luxon';
import { ObjectValues } from '../types';
import { getT, SUPPORTED_LANGUAGE, SupportedLanguage } from '../i18n';

export const RECURRING_INTERVAL_UNIT = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
} as const;

export const RECURRING_END_TYPE = {
  NEVER: 'never',
  ON_DATE: 'on_date',
  REPEATS: 'repeats',
} as const;

export const recurringIntervalUnits = ['day', 'week', 'month', 'year'] as const;
export const recurringEndTypes = ['never', 'on_date', 'repeats'] as const;

export type RecurringIntervalUnit = ObjectValues<typeof RECURRING_INTERVAL_UNIT>;
export type RecurringEndType = ObjectValues<typeof RECURRING_END_TYPE>;

export interface Occurrence {
  from: string;
  to: string;
}

export interface RecurrenceSettings {
  interval: {
    amount: number;
    unit: RecurringIntervalUnit;
    days?: number[];
  };
  end: {
    type: RecurringEndType;
    value?: string | number;
  };
  customTimes?: {
    perWeekday?: {
      // key is the dayOfWeek number, but string is required as key in json
      [key: string]: {
        startTime?: string;
        endTime?: string;
      };
    };
    perDate?: {
      [key: string]: {
        startTime?: string;
        endTime?: string;
      };
    };
  };
}

export function getRecurrenceSettingsLabel(settings: RecurrenceSettings, lng: SupportedLanguage = SUPPORTED_LANGUAGE.DE) {
  const t = getT(lng);

  let result = '';

  if (settings.interval.amount === 1) {
    result += t(`lib:recurringSettings.recurringSingularUnit.${settings.interval.unit}`);
  } else {
    result += `${t('lib:recurringSettings:genericEvery')} ${settings.interval.amount} ${t(`lib:recurringSettings:units.${settings.interval.unit}`)}`;
  }

  if (settings.interval.unit === RECURRING_INTERVAL_UNIT.WEEK && settings.interval.days) {
    const weekdayLabels = Info.weekdays('short', { locale: lng });
    const days = settings.interval.days
      .map((dayOfWeek) => weekdayLabels[dayOfWeek - 1])
      .join(', ');

    result += ` ${t('lib:recurringSettings.at')} ${days}`;
  }

  if (settings.end.type === RECURRING_END_TYPE.REPEATS && typeof settings.end.value === 'number') {
    result += `, ${settings.end.value} ${t('lib:recurringSettings.times')}`;
  }

  if (settings.end.type === RECURRING_END_TYPE.ON_DATE && typeof settings.end.value === 'string') {
    result += `, ${t('lib:recurringSettings.until')} ${DateTime.fromISO(settings.end.value).toFormat('dd. MMM. yyyy')}`;
  }

  return result;
}

export function getRecurrenceSettingsLongLabel(
  settings: RecurrenceSettings,
  occurrences: Occurrence[],
  lng: SupportedLanguage = SUPPORTED_LANGUAGE.DE,
) {
  const t = getT(lng);

  let result = '';

  if (settings.interval.amount === 1) {
    result += t(`lib:recurringSettings.recurringSingularUnit.${settings.interval.unit}`);
  } else {
    result += `${t('lib:recurringSettings:genericEvery')} ${settings.interval.amount} ${t(`lib:recurringSettings:units.${settings.interval.unit}`)}`;
  }

  if (settings.interval.unit === RECURRING_INTERVAL_UNIT.WEEK && settings.interval.days) {
    const weekdayLabels = Info.weekdays('long', { locale: lng });

    const days = settings.interval.days
      .map((dayOfWeek) => weekdayLabels[dayOfWeek - 1]);

    const daysString = (() => {
      if (days.length > 1) {
        const lastBothDays = days.splice(-2).join(` ${t('lib:recurringSettings.longVersion.and')} `);

        return [...days, lastBothDays].join(', ');
      }

      return days.join(', ');
    })();

    result += ` ${t('lib:recurringSettings.at')} ${daysString}`;
  }

  if (settings.end.type === RECURRING_END_TYPE.REPEATS && typeof settings.end.value === 'number') {
    const lastOccurrence = [...occurrences].pop();

    if (lastOccurrence) {
      result += `. ${t('lib:recurringSettings.longVersion.lastOccurrence')} ${DateTime.fromISO(lastOccurrence.from).toFormat('dd. MMMM yyyy')}.`;
    } else {
      result += `. ${t('lib:recurringSettings.longVersion.total', { times: settings.end.value })}`;
    }
  }

  if (settings.end.type === RECURRING_END_TYPE.ON_DATE && typeof settings.end.value === 'string') {
    result += `. ${t('lib:recurringSettings.longVersion.lastOccurrence')} ${DateTime.fromISO(settings.end.value).toFormat('dd. MMMM yyyy')}.`;
  }

  return result;
}

export function generateRecurringDates(settings: RecurrenceSettings, startDate: Date, endDate?: Date) {
  const start = DateTime.fromJSDate(startDate);
  const end = endDate ? DateTime.fromJSDate(endDate) : undefined;

  const diff = end ? end.diff(start) : undefined;

  let shouldContinue = true;

  const rows: { from: DateTime, to: DateTime }[] = [];

  let latestStart = start;

  function endDateReached(from: DateTime): boolean {
    // break if manually set end date has been reached
    if (
      settings.end.type === RECURRING_END_TYPE.ON_DATE
      && typeof settings.end.value === 'string'
      && DateTime.fromISO(settings.end.value).endOf('day') <= from
    ) {
      return true;
    }

    // break if max end date (2 years) has been reached
    if (
      settings.end.type === RECURRING_END_TYPE.NEVER
      && DateTime.now().plus({ year: 2 }).endOf('day') <= from) {
      return true;
    }

    return false;
  }

  function repeatsReached(): boolean {
    if (settings.end.type === RECURRING_END_TYPE.REPEATS && typeof settings.end.value === 'number') {
      // decrease by one as the initial occurrence should also be counted
      const maxRepeats = settings.end.value - 1;

      // break if requested repeats have been reached
      if (maxRepeats <= rows.length) {
        return true;
      }
    }

    return false;
  }

  function getToDate(from: DateTime): DateTime {
    if (!end || !diff) {
      return from;
    }

    return from.plus(diff);
  }

  function applyCustomTime(date: DateTime, type: 'startTime' | 'endTime') {
    let adjustedDate = date;

    // check if there is a custom time set for the current weekday
    if (settings.customTimes?.perWeekday && settings.customTimes.perWeekday[date.weekday]?.[type]) {
      const timeForWeekday = settings.customTimes.perWeekday[date.weekday][type]!;

      const timeForWeekdayParts = timeForWeekday
        .split(':')
        .map((part) => parseInt(part));

      adjustedDate = adjustedDate.set({
        hour: timeForWeekdayParts[0],
        minute: timeForWeekdayParts[1],
        second: timeForWeekdayParts[2] ?? 0,
      });
    }

    // check if a custom time is set for that specific date
    const dateAsString = date.toISODate();
    if (settings.customTimes?.perDate && dateAsString && settings.customTimes.perDate[dateAsString]?.[type]) {
      const timeForDate = settings.customTimes.perDate[dateAsString][type]!;

      const timeForWeekdayParts = timeForDate
        .split(':')
        .map((part) => parseInt(part));

      adjustedDate = adjustedDate.set({
        hour: timeForWeekdayParts[0],
        minute: timeForWeekdayParts[1],
        second: timeForWeekdayParts[2] ?? 0,
      });
    }

    return adjustedDate;
  }

  function handleOccurrence(from: DateTime): boolean {
    // check if "from" date already exceeds the end date (if set), break if so
    if (endDateReached(from)) {
      return false;
    }

    const customFrom = applyCustomTime(from, 'startTime');
    const customTo = applyCustomTime(getToDate(customFrom), 'endTime');

    rows.push({ from: customFrom, to: customTo });

    // do NOT adjust the value used for latestStart so the following occurrences are not affected by custom settings
    latestStart = from;

    // check if the requested repeats have been reached (if set)
    return !repeatsReached();
  }

  function handleOccurrencesPerWeek(from: DateTime): boolean {
    if (settings.interval.unit === RECURRING_INTERVAL_UNIT.WEEK && settings.interval.days) {
      for (const dayOfWeek of settings.interval.days) {
        const forWeekday = from.set({
          weekday: dayOfWeek as WeekdayNumbers,
        });

        /* only handle the weekday if it's after the initial start.
           e.g. event created on friday, set for "weekly on Mo, Fri" --> the Monday in the first week shouldn't be handled
         */
        if (forWeekday > start) {
          shouldContinue = handleOccurrence(forWeekday);

          if (!shouldContinue) {
            return false;
          }
        }
      }
    }

    return true;
  }

  function getNextOccurrence(): boolean {
    let from = latestStart.plus({ [settings.interval.unit]: settings.interval.amount });

    // special handling for the weekly period as there can be multiple occurrences per week
    if (settings.interval.unit === RECURRING_INTERVAL_UNIT.WEEK) {
      return handleOccurrencesPerWeek(from);
    }

    // special handling for monthly or yearly period if the specified dayOfMonth (e.g. 31 or 29.02) doesn't exist in that month/year --> skip it
    if (([RECURRING_INTERVAL_UNIT.MONTH, RECURRING_INTERVAL_UNIT.YEAR] as RecurringIntervalUnit[]).includes(settings.interval.unit)) {
      let multiplier = 2;
      while (from.day !== latestStart.day) {
        from = latestStart.plus({ [settings.interval.unit]: settings.interval.amount * multiplier });
        multiplier += 1;
      }
    }

    return handleOccurrence(from);
  }

  // only a single repeat was request - we can break here as the default date values are used for that single occurrence
  if (settings.end.type === RECURRING_END_TYPE.REPEATS && typeof settings.end?.value === 'number' && settings.end.value <= 1) {
    return [];
  }

  /* handle "weekly" occurrences within the same week as the start date.
     example: event created on monday, repeating every monday and friday --> the friday of the same week should also be handled
   */
  if (settings.interval.unit === RECURRING_INTERVAL_UNIT.WEEK) {
    shouldContinue = handleOccurrencesPerWeek(start);
  }

  if (shouldContinue) {
    do {
      shouldContinue = getNextOccurrence();
    } while (shouldContinue);
  }

  return rows.map((row) => ({
    from: row.from.toUTC().toISO(),
    to: row.to.toUTC().toISO(),
  }));
}
